import React, { useState, useEffect } from 'react';
import { 
  FaFileContract, 
  FaSearch,
  FaFilter,
  FaPlus,
  FaEye,
  FaEnvelope,
  FaCheckCircle,
  FaTimes,
  FaClock,
  FaEdit,
  FaTrash,
  FaDownload,
  FaCalendar,
  FaUser,
  FaBuilding,
  FaUpload,
  FaSpinner,
  FaChevronLeft,
  FaChevronRight,
  FaSignature
} from 'react-icons/fa';
import { API_ENDPOINTS } from '../../config/api';
import PeaknetSignModal from './PeaknetSignModal';

const ContractManagement = () => {
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedContract, setSelectedContract] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showSignModal, setShowSignModal] = useState(false);
  const [editingContract, setEditingContract] = useState(null);
  const [signingContract, setSigningContract] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  useEffect(() => {
    loadContracts();
  }, []);

  const loadContracts = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(API_ENDPOINTS.CONTRACT_MANAGEMENT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('adminToken') || ''}`
        },
        body: JSON.stringify({
          action: 'listContracts'
        })
      });

      const data = await response.json();

      if (data.success) {
        setContracts(data.contracts || []);
      } else {
        setError(data.error || 'Failed to load contracts');
      }
    } catch (err) {
      console.error('Error loading contracts:', err);
      setError('Failed to load contracts. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSendContract = async (contractId) => {
    if (!window.confirm('Send this contract to the customer via email?')) {
      return;
    }

    try {
      const response = await fetch(API_ENDPOINTS.CONTRACT_MANAGEMENT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('adminToken') || ''}`
        },
        body: JSON.stringify({
          action: 'sendContract',
          contractId: contractId
        })
      });

      const data = await response.json();

      if (data.success) {
        alert('Contract sent successfully!');
        loadContracts(); // Reload to update status
      } else {
        alert(data.error || 'Failed to send contract');
      }
    } catch (err) {
      console.error('Error sending contract:', err);
      alert('Failed to send contract. Please try again.');
    }
  };

  const handleViewContract = async (contractId) => {
    try {
      const response = await fetch(API_ENDPOINTS.CONTRACT_MANAGEMENT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('adminToken') || ''}`
        },
        body: JSON.stringify({
          action: 'getContract',
          contractId: contractId
        })
      });

      const data = await response.json();

      if (data.success && data.contract) {
        setSelectedContract(data.contract);
        setShowViewModal(true);
      } else {
        alert(data.error || 'Failed to load contract');
      }
    } catch (err) {
      console.error('Error loading contract:', err);
      alert('Failed to load contract. Please try again.');
    }
  };

  const handleSignContract = async (contractId) => {
    try {
      const response = await fetch(API_ENDPOINTS.CONTRACT_MANAGEMENT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('adminToken') || ''}`
        },
        body: JSON.stringify({
          action: 'getContract',
          contractId: contractId
        })
      });

      const data = await response.json();

      if (data.success && data.contract) {
        setSigningContract(data.contract);
        setShowSignModal(true);
      } else {
        alert(data.error || 'Failed to load contract');
      }
    } catch (err) {
      console.error('Error loading contract:', err);
      alert('Failed to load contract. Please try again.');
    }
  };

  const handleEditContract = async (contractId) => {
    try {
      const response = await fetch(API_ENDPOINTS.CONTRACT_MANAGEMENT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('adminToken') || ''}`
        },
        body: JSON.stringify({
          action: 'getContract',
          contractId: contractId
        })
      });

      const data = await response.json();

      if (data.success && data.contract) {
        setEditingContract(data.contract);
        setShowEditModal(true);
      } else {
        alert(data.error || 'Failed to load contract');
      }
    } catch (err) {
      console.error('Error loading contract:', err);
      alert('Failed to load contract. Please try again.');
    }
  };

  const handleDeleteContract = async (contractId) => {
    if (!window.confirm('Are you sure you want to delete this contract?')) {
      return;
    }

    try {
      const response = await fetch(API_ENDPOINTS.CONTRACT_MANAGEMENT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('adminToken') || ''}`
        },
        body: JSON.stringify({
          action: 'deleteContract',
          contractId: contractId
        })
      });

      const data = await response.json();

      if (data.success) {
        alert('Contract deleted successfully!');
        loadContracts();
      } else {
        alert(data.error || 'Failed to delete contract');
      }
    } catch (err) {
      console.error('Error deleting contract:', err);
      alert('Failed to delete contract. Please try again.');
    }
  };

  const handleContractSaved = () => {
    setShowCreateModal(false);
    setShowEditModal(false);
    setEditingContract(null);
    loadContracts();
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const badges = {
      draft: { bg: 'bg-gray-500/20', text: 'text-gray-300', label: 'Draft' },
      sent: { bg: 'bg-blue-500/20', text: 'text-blue-300', label: 'Sent' },
      customer_signed: { bg: 'bg-yellow-500/20', text: 'text-yellow-300', label: 'Customer Signed' },
      signed: { bg: 'bg-green-500/20', text: 'text-green-300', label: 'Fully Signed' },
      completed: { bg: 'bg-purple-500/20', text: 'text-purple-300', label: 'Completed' },
      cancelled: { bg: 'bg-red-500/20', text: 'text-red-300', label: 'Cancelled' }
    };

    const badge = badges[status] || badges.draft;
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${badge.bg} ${badge.text}`}>
        {badge.label}
      </span>
    );
  };

  // Filter contracts
  const filteredContracts = contracts.filter(contract => {
    const matchesSearch = 
      contract.contractNumber?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      contract.customerName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      contract.customerEmail?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      contract.title?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || contract.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  // Pagination
  const totalPages = Math.ceil(filteredContracts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedContracts = filteredContracts.slice(startIndex, startIndex + itemsPerPage);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <FaSpinner className="animate-spin text-4xl text-[#FFD700]" />
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-[#ebdbb2] mb-2">Contract Management</h1>
        <p className="text-[#a89984]">Manage contracts, send to customers, and track signatures</p>
      </div>

      {/* Actions Bar */}
      <div className="bg-[#3c3836] rounded-lg p-4 mb-6 flex flex-col md:flex-row gap-4 items-center justify-between">
        <div className="flex flex-1 gap-4 w-full md:w-auto">
          {/* Search */}
          <div className="relative flex-1 md:flex-initial">
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#a89984]" />
            <input
              type="text"
              placeholder="Search contracts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-4 py-2 bg-[#282828] border border-[#504945] rounded-lg text-[#ebdbb2] placeholder-[#a89984] focus:outline-none focus:border-[#FFD700] w-full md:w-64"
            />
          </div>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 bg-[#282828] border border-[#504945] rounded-lg text-[#ebdbb2] focus:outline-none focus:border-[#FFD700]"
          >
            <option value="all">All Status</option>
            <option value="draft">Draft</option>
            <option value="sent">Sent</option>
            <option value="signed">Signed</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>

        <button
          onClick={() => setShowCreateModal(true)}
          className="bg-gradient-to-r from-[#FFD700] to-[#FFB700] text-[#1d2021] px-6 py-2 rounded-lg font-semibold hover:shadow-lg transition-all flex items-center space-x-2"
        >
          <FaPlus />
          <span>Create Contract</span>
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-4 mb-6">
          <p className="text-red-300">{error}</p>
        </div>
      )}

      {/* Contracts Table */}
      <div className="bg-[#282828] rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#3c3836]">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#a89984] uppercase tracking-wider">Contract #</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#a89984] uppercase tracking-wider">Title</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#a89984] uppercase tracking-wider">Customer</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#a89984] uppercase tracking-wider">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#a89984] uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#a89984] uppercase tracking-wider">Sent Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#a89984] uppercase tracking-wider">Signed Date</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-[#a89984] uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#3c3836]">
              {paginatedContracts.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-6 py-8 text-center text-[#a89984]">
                    No contracts found
                  </td>
                </tr>
              ) : (
                paginatedContracts.map((contract) => (
                  <tr key={contract.contractId} className="hover:bg-[#3c3836]">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-[#ebdbb2]">
                      {contract.contractNumber || contract.contractId}
                    </td>
                    <td className="px-6 py-4 text-sm text-[#ebdbb2]">
                      {contract.title || 'Untitled Contract'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-[#ebdbb2]">
                      {contract.customerName || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-[#ebdbb2]">
                      {contract.customerEmail || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(contract.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-[#ebdbb2]">
                      {formatDate(contract.sentAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-[#ebdbb2]">
                      {formatDate(contract.customerSignedAt || contract.signedAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => handleViewContract(contract.contractId)}
                          className="text-[#FFD700] hover:text-[#FFB700] p-2"
                          title="View"
                        >
                          <FaEye />
                        </button>
                        {contract.status === 'draft' && (
                          <>
                            <button
                              onClick={() => handleEditContract(contract.contractId)}
                              className="text-blue-400 hover:text-blue-300 p-2"
                              title="Edit"
                            >
                              <FaEdit />
                            </button>
                            <button
                              onClick={() => handleDeleteContract(contract.contractId)}
                              className="text-red-400 hover:text-red-300 p-2"
                              title="Delete"
                            >
                              <FaTrash />
                            </button>
                          </>
                        )}
                        {contract.status === 'draft' && (
                          <button
                            onClick={() => handleSendContract(contract.contractId)}
                            className="text-green-400 hover:text-green-300 p-2"
                            title="Send to Customer"
                          >
                            <FaEnvelope />
                          </button>
                        )}
                        {contract.status === 'customer_signed' && (
                          <button
                            onClick={() => handleSignContract(contract.contractId)}
                            className="text-yellow-400 hover:text-yellow-300 p-2"
                            title="Sign as Peaknet"
                          >
                            <FaSignature />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-[#3c3836] px-6 py-4 flex items-center justify-between">
            <div className="text-sm text-[#a89984]">
              Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, filteredContracts.length)} of {filteredContracts.length} contracts
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="px-4 py-2 bg-[#282828] border border-[#504945] rounded-lg text-[#ebdbb2] disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#3c3836]"
              >
                <FaChevronLeft />
              </button>
              <span className="px-4 py-2 text-[#ebdbb2]">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="px-4 py-2 bg-[#282828] border border-[#504945] rounded-lg text-[#ebdbb2] disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#3c3836]"
              >
                <FaChevronRight />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {(showCreateModal || showEditModal) && (
        <ContractForm
          contract={editingContract}
          onSave={handleContractSaved}
          onCancel={() => {
            setShowCreateModal(false);
            setShowEditModal(false);
            setEditingContract(null);
          }}
        />
      )}

      {/* View Modal */}
      {showViewModal && selectedContract && (
        <ContractViewModal
          contract={selectedContract}
          onClose={() => {
            setShowViewModal(false);
            setSelectedContract(null);
          }}
        />
      )}

      {/* Sign Modal */}
      {showSignModal && signingContract && (
        <PeaknetSignModal
          contract={signingContract}
          onClose={() => {
            setShowSignModal(false);
            setSigningContract(null);
          }}
          onSigned={() => {
            setShowSignModal(false);
            setSigningContract(null);
            loadContracts(); // Reload to update status
          }}
        />
      )}
    </div>
  );
};

// Contract Form Component
const ContractForm = ({ contract, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    title: contract?.title || '',
    customerEmail: contract?.customerEmail || '',
    customerName: contract?.customerName || '',
    description: contract?.description || '',
    contractFile: null
  });
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        setError('Please upload a PDF file');
        return;
      }
      if (file.size > 10 * 1024 * 1024) { // 10MB limit
        setError('File size must be less than 10MB');
        return;
      }
      setFormData({ ...formData, contractFile: file });
      setError(null);
    }
  };

  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result.split(',')[1]);
      reader.onerror = error => reject(error);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!formData.title || !formData.customerEmail || !formData.customerName) {
      setError('Please fill in all required fields');
      return;
    }

    if (!contract && !formData.contractFile) {
      setError('Please upload a contract file');
      return;
    }

    try {
      setUploading(true);

      let contractFileBase64 = null;
      if (formData.contractFile) {
        contractFileBase64 = await convertFileToBase64(formData.contractFile);
      }

      const payload = {
        action: contract ? 'updateContract' : 'createContract',
        ...(contract && { contractId: contract.contractId }),
        title: formData.title,
        customerEmail: formData.customerEmail,
        customerName: formData.customerName,
        description: formData.description || '',
        contractFile: contractFileBase64,
        fileName: formData.contractFile?.name || contract?.fileName
      };

      const response = await fetch(API_ENDPOINTS.CONTRACT_MANAGEMENT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('adminToken') || ''}`
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (data.success) {
        onSave();
      } else {
        setError(data.error || 'Failed to save contract');
      }
    } catch (err) {
      console.error('Error saving contract:', err);
      setError('Failed to save contract. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-[#282828] rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-[#3c3836]">
          <h2 className="text-2xl font-bold text-[#ebdbb2]">
            {contract ? 'Edit Contract' : 'Create New Contract'}
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-4">
              <p className="text-red-300">{error}</p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-[#a89984] mb-2">
              Contract Title <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-4 py-2 bg-[#1d2021] border border-[#504945] rounded-lg text-[#ebdbb2] focus:outline-none focus:border-[#FFD700]"
              placeholder="Service Agreement, Terms of Service, etc."
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[#a89984] mb-2">
              Customer Name <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={formData.customerName}
              onChange={(e) => setFormData({ ...formData, customerName: e.target.value })}
              className="w-full px-4 py-2 bg-[#1d2021] border border-[#504945] rounded-lg text-[#ebdbb2] focus:outline-none focus:border-[#FFD700]"
              placeholder="John Doe"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[#a89984] mb-2">
              Customer Email(s) <span className="text-red-400">*</span>
            </label>
            <textarea
              value={formData.customerEmail}
              onChange={(e) => setFormData({ ...formData, customerEmail: e.target.value })}
              className="w-full px-4 py-2 bg-[#1d2021] border border-[#504945] rounded-lg text-[#ebdbb2] focus:outline-none focus:border-[#FFD700]"
              placeholder="customer@example.com, another@example.com"
              rows="2"
              required
            />
            <p className="text-xs text-[#a89984] mt-1">
              Enter multiple email addresses separated by commas
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-[#a89984] mb-2">
              Description (Optional)
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-4 py-2 bg-[#1d2021] border border-[#504945] rounded-lg text-[#ebdbb2] focus:outline-none focus:border-[#FFD700]"
              rows="3"
              placeholder="Additional notes about this contract..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[#a89984] mb-2">
              Contract File (PDF) {!contract && <span className="text-red-400">*</span>}
            </label>
            <div className="flex items-center space-x-4">
              <label className="flex-1 cursor-pointer">
                <input
                  type="file"
                  accept=".pdf"
                  onChange={handleFileChange}
                  className="hidden"
                />
                <div className="px-4 py-2 bg-[#1d2021] border border-[#504945] rounded-lg text-[#ebdbb2] hover:bg-[#3c3836] flex items-center justify-center space-x-2">
                  <FaUpload />
                  <span>{formData.contractFile?.name || contract?.fileName || 'Choose PDF file'}</span>
                </div>
              </label>
              {contract?.fileName && !formData.contractFile && (
                <span className="text-sm text-[#a89984]">Current: {contract.fileName}</span>
              )}
            </div>
            <p className="text-xs text-[#a89984] mt-1">Maximum file size: 10MB</p>
          </div>

          <div className="flex justify-end space-x-4 pt-4 border-t border-[#3c3836]">
            <button
              type="button"
              onClick={onCancel}
              className="px-6 py-2 bg-[#3c3836] text-[#ebdbb2] rounded-lg hover:bg-[#504945] transition-colors"
              disabled={uploading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-gradient-to-r from-[#FFD700] to-[#FFB700] text-[#1d2021] rounded-lg font-semibold hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              disabled={uploading}
            >
              {uploading && <FaSpinner className="animate-spin" />}
              <span>{contract ? 'Update Contract' : 'Create Contract'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Contract View Modal Component
const ContractViewModal = ({ contract, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-[#282828] rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-[#3c3836] flex justify-between items-center">
          <h2 className="text-2xl font-bold text-[#ebdbb2]">Contract Details</h2>
          <button
            onClick={onClose}
            className="text-[#a89984] hover:text-[#ebdbb2]"
          >
            <FaTimes size={20} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#a89984] mb-1">Contract Number</label>
              <p className="text-[#ebdbb2]">{contract.contractNumber || contract.contractId}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#a89984] mb-1">Status</label>
              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                contract.status === 'signed' ? 'bg-green-500/20 text-green-300' :
                contract.status === 'customer_signed' ? 'bg-yellow-500/20 text-yellow-300' :
                contract.status === 'sent' ? 'bg-blue-500/20 text-blue-300' :
                'bg-gray-500/20 text-gray-300'
              }`}>
                {contract.status === 'customer_signed' ? 'Customer Signed' : contract.status}
              </span>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#a89984] mb-1">Customer Name</label>
              <p className="text-[#ebdbb2]">{contract.customerName}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#a89984] mb-1">Customer Email</label>
              <p className="text-[#ebdbb2]">{contract.customerEmail}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#a89984] mb-1">Sent Date</label>
              <p className="text-[#ebdbb2]">{contract.sentAt ? new Date(contract.sentAt).toLocaleString('en-AU') : 'Not sent'}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#a89984] mb-1">Customer Signed Date</label>
              <p className="text-[#ebdbb2]">
                {contract.customerSignedAt ? new Date(contract.customerSignedAt).toLocaleString('en-AU') : 
                 contract.signedAt ? new Date(contract.signedAt).toLocaleString('en-AU') : 'Not signed'}
              </p>
            </div>
            {contract.peaknetSignedAt && (
              <div>
                <label className="block text-sm font-medium text-[#a89984] mb-1">Peaknet Signed Date</label>
                <p className="text-[#ebdbb2]">{new Date(contract.peaknetSignedAt).toLocaleString('en-AU')}</p>
              </div>
            )}
            {contract.signedCompanyName && (
              <div>
                <label className="block text-sm font-medium text-[#a89984] mb-1">Company Name</label>
                <p className="text-[#ebdbb2]">{contract.signedCompanyName}</p>
              </div>
            )}
            {contract.signedAuthorizedPerson && (
              <div>
                <label className="block text-sm font-medium text-[#a89984] mb-1">Authorized Person</label>
                <p className="text-[#ebdbb2]">{contract.signedAuthorizedPerson}</p>
              </div>
            )}
          </div>

          {contract.description && (
            <div>
              <label className="block text-sm font-medium text-[#a89984] mb-1">Description</label>
              <p className="text-[#ebdbb2]">{contract.description}</p>
            </div>
          )}

          {(contract.signedContractS3Key || contract.signedContractUrl) && (
            <div>
              <button
                onClick={async () => {
                  try {
                    // Get fresh PDF URL
                    const response = await fetch(API_ENDPOINTS.CONTRACT_MANAGEMENT, {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('adminToken') || ''}`
                      },
                      body: JSON.stringify({
                        action: 'getContractPdfUrl',
                        contractId: contract.contractId,
                        pdfType: 'signed'
                      })
                    });

                    const data = await response.json();
                    if (data.success && data.url) {
                      // Use direct link approach to avoid CORS issues
                      const link = document.createElement('a');
                      link.href = data.url;
                      link.download = data.fileName || `signed-contract-${contract.contractId}.pdf`;
                      link.target = '_blank'; // Fallback to opening in new tab if download doesn't work
                      link.rel = 'noopener noreferrer';
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                    } else {
                      alert('Failed to download contract. Please try again.');
                    }
                  } catch (err) {
                    console.error('Error downloading contract:', err);
                    alert('Failed to download contract. Please try again.');
                  }
                }}
                className="inline-flex items-center space-x-2 px-4 py-2 bg-[#FFD700] text-[#1d2021] rounded-lg hover:bg-[#FFB700] transition-colors cursor-pointer"
              >
                <FaDownload />
                <span>Download Signed Contract</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContractManagement;

